import type { ReactNode } from "react";
import { showNotification } from "@mantine/notifications";
import type { NotificationProps } from "@mantine/notifications";

export type MessageParameters = {
  title: string;
  message: string;
  autoClose?: number | false;
  /** Optional leading icon; renders in place of the coloured accent. */
  icon?: ReactNode;
};

// ---------------------------------------------------------------------------
// Notification functions.
//
// The ONLY place `showNotification` from @mantine/notifications is called in
// the kiosk app — everything else must go through `useMessages()` (or import
// these functions directly in non-component modules). Plain module-level
// functions (no React state/hooks); `useMessages()` returns references.
// ---------------------------------------------------------------------------

/**
 * Shows a success notification (green house style, default autoClose 5000ms)
 */
export const showSuccessMessage = ({
  title,
  message,
  autoClose = 5000,
  icon,
}: MessageParameters) =>
  showNotification({
    title,
    message,
    icon,
    sx: { backgroundColor: "#1F6521", color: "white" },
    styles: {
      root: {
        "&::before": { backgroundColor: "#1F6521" },
        borderRadius: "10px",
      },
      title: { color: "white", fontWeight: 600 },
      description: { color: "white" },
    },
    autoClose,
    radius: "md",
    color: "#1F6521",
  });

/**
 * Shows an error notification (red house style, default autoClose 8000ms).
 * Pass `autoClose: false` for a persistent, must-dismiss error.
 */
export const showErrorMessage = ({
  title,
  message,
  autoClose = 8000,
  icon,
}: MessageParameters) =>
  showNotification({
    title,
    message,
    icon,
    sx: { backgroundColor: "#c92a2a", color: "white" },
    styles: {
      root: {
        "&::before": { backgroundColor: "#c92a2a" },
        borderRadius: "10px",
      },
      title: { color: "white", fontWeight: 600 },
      description: { color: "white" },
    },
    autoClose,
    radius: "md",
    color: "red",
  });

/**
 * Shows an informational notification (blue house style, default autoClose 5000ms)
 */
export const showInfoMessage = ({
  title,
  message,
  autoClose = 5000,
  icon,
}: MessageParameters) =>
  showNotification({
    title,
    message,
    icon,
    sx: { backgroundColor: "#1c7ed6", color: "white" },
    styles: {
      root: {
        "&::before": { backgroundColor: "#1c7ed6" },
        borderRadius: "10px",
      },
      title: { color: "white", fontWeight: 600 },
      description: { color: "white" },
    },
    autoClose,
    radius: "md",
    color: "blue",
  });

/**
 * Generic notification passthrough for one-off notifications that don't fit the
 * semantic helpers (custom color/styling/autoClose). Accepts full Mantine
 * notification options; returns the id.
 */
export const showMessage = (options: NotificationProps) =>
  showNotification(options);

/**
 * Primary interface for showing notification messages in the kiosk app. Prefer
 * this over calling `showNotification` from `@mantine/notifications` directly
 * (that is lint-banned outside this file).
 */
export const useMessages = () => {
  return {
    showSuccessMessage,
    showErrorMessage,
    showInfoMessage,
    showMessage,
  };
};
